import { Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

/**
 * What the editor can say about the document on screen at one moment, for the asynchronous export-time pre-flight
 * check (IPF-5). Plain values only, so the flow below can be tested without the component.
 */
export interface ExportAttemptContext {
  /** The document on screen. */
  docId: number;
  /** `preflightSignature()` of it: lines, delivery date, company. */
  signature: string;
  /** The editor is busy: a row is being edited or the content is loading. */
  busy: boolean;
  /** No unsaved header edits other than the `DOCFLAG` the check itself changes. */
  headerClean: boolean;
  /** Any row highlighted by the editor's own checks, read now. */
  rowsHighlighted: boolean;
}

export interface ExportPreflightFlowDeps {
  /** The current state of the editor, or null when no document is loaded. */
  context(): ExportAttemptContext | null;
  /** Runs the pre-flight check and answers its `maxSeverity`. */
  request(): Observable<number>;
  /** Hands over to the gate with the row flags read at the END of the attempt. */
  decide(rowsHighlighted: boolean, severity: number): void;
  /** The check failed: the user is told, and the gate continues without it. */
  warnFailed(): void;
  /** The document was edited while the check ran: the user is told to press the button again. */
  warnChanged(): void;
}

/**
 * The asynchronous part of the "На печать" gate (IPF-5): run the check, then continue only if the document on screen is
 * still the one that was checked, in the state it was checked in. A response that arrives after an edit (the lines, the
 * delivery date, the company or any other header field), after the editor became busy, or after another invoice was
 * loaded into the same component is discarded, so the gate always describes the invoice being printed
 * (PR review, owner 2026-10-22). Never a hard block: a failed check continues without it.
 */
export class ExportPreflightFlow {
  private sub: Subscription | null = null;
  private running = false;

  constructor(private readonly deps: ExportPreflightFlowDeps) { }

  /** True while a check is running; the print and check buttons are disabled meanwhile. */
  get pending(): boolean { return this.running; }

  start(): void {
    const begun = this.deps.context();
    if (!begun) { return; }
    this.cancel();
    this.running = true;
    const sub = this.deps.request()
      .pipe(finalize(() => { this.running = false; }))
      .subscribe({
        next: severity => this.settle(begun, severity, false),
        error: () => this.settle(begun, 0, true),
      });
    this.sub = this.running ? sub : null;
  }

  /** Drops a pending check: its answer is never used. Called when another document is loaded or the editor is destroyed. */
  cancel(): void {
    if (this.sub) { this.sub.unsubscribe(); }
    this.sub = null;
    this.running = false;
  }

  private settle(begun: ExportAttemptContext, severity: number, failed: boolean): void {
    const now = this.deps.context();
    const same = !!now && now.docId === begun.docId && now.signature === begun.signature && !now.busy && now.headerClean;
    if (!same) {
      // Same invoice, changed under the check: tell the user. Another invoice: say nothing, it is not their print.
      if (now && now.docId === begun.docId) { this.deps.warnChanged(); }
      return;
    }
    if (failed) { this.deps.warnFailed(); }
    this.deps.decide(now.rowsHighlighted, severity);
  }
}
